import { useEffect, useRef, useState, type FormEvent } from 'react';
import { createFileRoute, redirect, useNavigate, useRouter } from '@tanstack/react-router';
import { useTranslations } from 'use-intl';
import { Body, Button, Card, Field, I, Input, Stack } from '@leanin/ui';
import { EntryJourneyScreen } from '@/components/entry-journeys';
import { PlacePicker, type PlaceSelection } from '@/components/geo';
import { useCreateCircle } from '@/lib/mutations/groups';
import {
  readCreatedCircleId,
  writeCreatedCircleId,
} from '@/components/onboarding/created-circle';
import { circleSaveErrorMessage } from '@/components/circles/circle-save-error';
import {
  trackOnboardingStepCompleted,
  trackOnboardingStepViewed,
} from '@/lib/analytics/events';
import { journeyStepParams } from '@/lib/analytics/onboarding-steps';
import { readEntryJourney } from '@/lib/entry-journey';

// Ad-journey onboarding, start-circle only — the design's minimal "Set up
// your Circle" card, following the about step. Creates a real Circle row
// with the caller as leader (same mutation + created-circle stash as the cl
// tour's create-circle step), then advances to /onboarding/j/youre-in.
// Journeys without a circle to create belong on the about step; a direct
// hit with no journey in sessionStorage falls back to the organic flow.
export const Route = createFileRoute('/onboarding/j/circle')({
  beforeLoad: () => {
    const journey = readEntryJourney();
    if (journey === null) {
      throw redirect({ to: '/onboarding/profile' });
    }
    if (journey !== 'start-circle') {
      throw redirect({ to: '/onboarding/j/about' });
    }
  },
  component: JourneyCirclePage,
});

function JourneyCirclePage() {
  const t = useTranslations('EntryJourneys');
  const navigate = useNavigate();
  const router = useRouter();
  const createCircle = useCreateCircle();
  const [name, setName] = useState('');
  const [place, setPlace] = useState<PlaceSelection | null>(null);
  const [error, setError] = useState<string | null>(null);
  // A circle already created in this onboarding session (a back-navigation
  // to this step): skip creation and just advance — the commit happened.
  const [createdCircleId, setCreatedCircleId] = useState<string | null>(() =>
    readCreatedCircleId('journey'),
  );

  const viewTrackedRef = useRef(false);
  useEffect(() => {
    if (viewTrackedRef.current) return;
    viewTrackedRef.current = true;
    trackOnboardingStepViewed(
      journeyStepParams('start-circle', '/onboarding/j/circle'),
    );
  }, []);

  const canProceed = createdCircleId !== null || name.trim().length > 0;
  const isPending = createCircle.isPending;

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!canProceed || isPending) return;
    setError(null);
    if (createdCircleId !== null) {
      trackOnboardingStepCompleted(
        journeyStepParams('start-circle', '/onboarding/j/circle'),
      );
      void navigate({ to: '/onboarding/j/youre-in' });
      return;
    }
    createCircle.mutate(
      {
        name: name.trim(),
        place,
        topicIds: [],
        access: 'public',
        // Journey defaults for everything the design's minimal card doesn't
        // ask: open to new members (request-to-join), not company-affiliated,
        // and a meeting format inferred from whether a place was chosen. All
        // refinable in the Circle's admin settings after onboarding.
        joinPolicy: 'request',
        affiliatedWithCompany: false,
        affiliatedCompany: null,
        format: place !== null ? 'in_person' : 'virtual',
      },
      {
        onSuccess: (detail) => {
          writeCreatedCircleId('journey', detail.id);
          setCreatedCircleId(detail.id);
          trackOnboardingStepCompleted(
            journeyStepParams('start-circle', '/onboarding/j/circle'),
          );
          void navigate({ to: '/onboarding/j/youre-in' });
        },
        onError: (err) => {
          setError(circleSaveErrorMessage(err, t('onboarding.circle.createError')));
        },
      },
    );
  };

  return (
    // No header block — the stepper's "Create your Circle" names the step;
    // the card is the whole screen.
    <EntryJourneyScreen journey="start-circle" current="circle-page">
      <Card style={{ width: '100%', maxWidth: 440 }}>
        {/* noValidate: the required-ness rule is "name OR a circle already
            created this session" (canProceed) — native validation can't
            express the second arm and would block the back-navigation
            advance on the empty name field. */}
        <form onSubmit={onSubmit} noValidate>
          <Stack gap="lg">
            <Field label={t('onboarding.circle.nameLabel')} required>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('onboarding.circle.namePlaceholder')}
                maxLength={120}
                required
              />
            </Field>
            <Field
              label={t('onboarding.circle.locationLabel')}
              description={t('onboarding.circle.membersNote')}
            >
              <PlacePicker
                mode="city"
                value={place}
                onChange={setPlace}
                placeholder={t('onboarding.circle.locationPlaceholder')}
              />
            </Field>
            {error !== null ? (
              <Body size="sm" color="destructive">
                {error}
              </Body>
            ) : null}
            <Button
              variant="primary"
              type="submit"
              iconRight={I.arrow}
              disabled={!canProceed}
              loading={isPending}
              block
            >
              {t('onboarding.circle.cta')}
            </Button>
            <Button
              variant="ghost"
              type="button"
              block
              onClick={() => router.history.back()}
            >
              {t('onboarding.circle.back')}
            </Button>
          </Stack>
        </form>
      </Card>
    </EntryJourneyScreen>
  );
}
